import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import i18n from '../i18n';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';
import { supabase } from '../lib/supabase/client';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  toggleLanguage: () => void; // Keep for backward compatibility
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  toggleLanguage: () => {},
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Use i18n.language directly as the source of truth
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    const lang = i18n.language?.split('-')[0] || 'en';
    return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage) ? lang as SupportedLanguage : 'en';
  });

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const baseLang = lng.split('-')[0] as SupportedLanguage;
      if (SUPPORTED_LANGUAGES.includes(baseLang)) {
        setLanguageState(baseLang);
      }
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // Sync current language to Supabase on mount if user is authenticated
  // This ensures existing users have their language preference stored
  useEffect(() => {
    const syncOnMount = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const currentLang = i18n.language?.split('-')[0] as SupportedLanguage;
          const storedLang = session.user.user_metadata?.language;
          // Only update if language is not already stored or is different
          if (!storedLang || storedLang !== currentLang) {
            await supabase.auth.updateUser({
              data: { language: currentLang }
            });
          }
        }
      } catch (error) {
        // Silent fail
      }
    };
    syncOnMount();
  }, []);

  // Sync language to Supabase user_metadata when user is authenticated
  const syncLanguageToSupabase = useCallback(async (lang: SupportedLanguage) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await supabase.auth.updateUser({
          data: { language: lang }
        });
      }
    } catch (error) {
      // Silent fail - language sync to Supabase is optional
      console.debug('[Language] Could not sync to Supabase:', error);
    }
  }, []);

  const setLanguage = useCallback((newLang: SupportedLanguage) => {
    if (SUPPORTED_LANGUAGES.includes(newLang)) {
      i18n.changeLanguage(newLang);
      // Also sync to Supabase for server-side access (email notifications, etc.)
      syncLanguageToSupabase(newLang);
    }
  }, [syncLanguageToSupabase]);

  // Keep toggleLanguage for backward compatibility (cycles through languages)
  const toggleLanguage = useCallback(() => {
    const currentIndex = SUPPORTED_LANGUAGES.indexOf(language);
    const nextIndex = (currentIndex + 1) % SUPPORTED_LANGUAGES.length;
    const newLang = SUPPORTED_LANGUAGES[nextIndex];
    i18n.changeLanguage(newLang);
    syncLanguageToSupabase(newLang);
  }, [language, syncLanguageToSupabase]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  // Also get the current language from i18n to ensure sync
  const currentLang = (i18n.language?.split('-')[0] || 'en') as SupportedLanguage;
  return { ...context, currentLang };
}
